const getIntervalName = ({ from, to }) => {
  if (from?.value === "-inf") {
    const operator = to?.inclusive ? "≤" : "<";
    return `${operator} ${to?.value}`;
  } else if (to?.value === "+inf") {
    const operator = from?.inclusive ? "≥" : ">";
    return `${operator} ${from?.value}`;
  }
  const leftOperator = from?.inclusive ? "≤" : "<";
  const rightOperator = to?.inclusive ? "≤" : "<";
  return `${from?.value} ${leftOperator} x ${rightOperator} ${to?.value}`;
};

const getSortScore = item => {
  if (item.influence_classification === "positive") {
    if (item.strength_classification === "strong") return 2;
    else if (item.strength_classification === "medium") return 1;

    return 0;
  }

  if (item.strength_classification === "strong") return -1;
  else if (item.strength_classification === "medium") return -2;

  return -3;
};

const getEntryValueById = (item, id) => {
  switch (item["@type"]) {
    case "numeric":
      return getIntervalName(
        item.intervals.find(interval => interval.id === id)?.interval
      );
    case "categorical":
      return item.values.find(value => value.id === id).name;
    default:
      return "";
  }
};

const getOrdinalName = (interval, values) => {
  if (interval.from > interval.to) {
    const temp = interval.to;
    interval.to = interval.from;
    interval.from = temp;
  }

  const fromValue = values.find(val => val.id === interval.from);
  const toValue =
    interval.to === 999999
      ? values
          .filter(val => val.id <= 999999)
          .reduce((max, val) => (!max || val.id > max.id ? val : max), null)
      : values.find(val => val.id === interval.to);

  if (!fromValue || !toValue) {
    return `${interval.from} < x < ${interval.to}`;
  }

  const range = toValue.id - fromValue.id;
  if (range > 1) return `from ${fromValue.name} to ${toValue.name}`;
  else if (range === 1) return `${fromValue.name} and ${toValue.name}`;

  return `${fromValue.name}`;
};

const getCategoricalRows = node =>
  node.values.map(item => ({
    key: item.id,
    id: item.id,
    value: item.name,
    influence: item.influence_classification,
    strength: item.strength_classification,
    score: getSortScore(item),
  }));

const getNumericRows = node =>
  node.intervals.map(item => ({
    key: item.id,
    id: item.id,
    value: getIntervalName(item.interval),
    influence: item.influence_classification,
    strength: item.strength_classification,
    score: getSortScore(item),
  }));

const getOrdinalRows = node => {
  const isolated = node.isolated_points?.map(point => {
    const value = node.values.find(val => val.id === point.value)?.name;
    return {
      key: point.id,
      id: point.id,
      value: value || "Blank value",
      influence: point.influence_classification,
      strength: point.strength_classification,
      score: getSortScore(point),
    };
  });
  const intervals = node.intervals?.map(item => ({
    key: item.id,
    id: item.id,
    value: getOrdinalName(item, node.values),
    influence: item.influence_classification,
    strength: item.strength_classification,
    score: getSortScore(item),
  }));
  return [...isolated, ...intervals];
};

const getBinaryMergeRows = (node, graphMap, driverMap) => {
  const leftGraphNode = graphMap[node.left];
  const rightGraphNode = graphMap[node.right];

  if (
    !leftGraphNode ||
    !rightGraphNode ||
    leftGraphNode?.node_type["@type"] === "binary_merge" ||
    rightGraphNode?.node_type["@type"] === "binary_merge"
  ) {
    return [];
  }

  const leftLabel =
    leftGraphNode.node_type?.column_name || leftGraphNode.id.slice(1);
  const rightLabel =
    rightGraphNode.node_type?.column_name || rightGraphNode.id.slice(1);

  const leftDriverItem = driverMap[leftLabel] ?? driverMap[`@${leftLabel}`];
  const rightDriverItem = driverMap[rightLabel] ?? driverMap[`@${rightLabel}`];

  return node.values.map((item, index) => ({
    key: `${node.id}-${index}`,
    id: `${node.id}-${index}`,
    [leftLabel]: getEntryValueById(leftDriverItem, item.left_id),
    [rightLabel]: getEntryValueById(rightDriverItem, item.right_id),
    influence: item.influence_classification,
    strength: item.strength_classification,
    score: getSortScore(item),
  }));
};

const getRows = (node, graphMap, driverMap) => {
  switch (node?.["@type"]) {
    case "categorical":
      return getCategoricalRows(node);
    case "numeric":
      return getNumericRows(node);
    case "ordinal":
      return getOrdinalRows(node);
    case "binary_merge":
      return getBinaryMergeRows(node, graphMap, driverMap);
    default:
      return [];
  }
};

export { getRows };
